import { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Autocomplete,
    Alert,
    Stack,
    LinearProgress,
    CircularProgress,
    Chip,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    TablePagination,
    Switch,
    IconButton,
    Tooltip,
    Snackbar,
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    Delete as DeleteIcon,
    CheckCircle as CheckIcon,
    Cancel as CancelIcon,
    Download as DownloadIcon,
    FilterList as FilterIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { Title, useGetIdentity } from 'react-admin';
import { supabase } from '../providers/supabase/supabase';
import { usePapaParse } from '../misc/usePapaParse';
import { useDiscountImport, DiscountCsvRow } from './useDiscountImport';

type DiscountRow = {
    id: number;
    aip: string;
    state: string;
    has_discount: boolean;
    discount: number | null;
    discount_deadline: string | null;
    premium_deadline: string | null;
    notes: string | null;
    excluded_crops: string[];
    excluded_products: string[];
    created_at: string;
};

type SortField = keyof Pick<
    DiscountRow,
    | 'aip'
    | 'state'
    | 'has_discount'
    | 'discount'
    | 'discount_deadline'
    | 'premium_deadline'
    | 'notes'
    | 'excluded_crops'
    | 'excluded_products'
>;

type SortDir = 'asc' | 'desc';

export const DiscountManagerPage = () => {
    const { identity } = useGetIdentity();
    const isAdmin = !!(identity as any)?.administrator;

    const [rows, setRows] = useState<DiscountRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [uploadOpen, setUploadOpen] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [sortField, setSortField] = useState<SortField>('aip');
    const [sortDir, setSortDir] = useState<SortDir>('asc');
    const [stateFilter, setStateFilter] = useState<string>('');
    const [snackMsg, setSnackMsg] = useState('');

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const PAGE_SIZE = 1000;
            let allData: DiscountRow[] = [];
            let from = 0;
            let hasMore = true;

            while (hasMore) {
                const { data, error: fetchError } = await supabase
                    .from('discount_manager')
                    .select('*')
                    .order('aip', { ascending: true })
                    .order('state', { ascending: true })
                    .range(from, from + PAGE_SIZE - 1);

                if (fetchError) throw fetchError;
                const page = (data as DiscountRow[]) ?? [];
                allData = allData.concat(page);
                hasMore = page.length === PAGE_SIZE;
                from += PAGE_SIZE;
            }

            setRows(allData);
        } catch (err: any) {
            console.error('Failed to fetch discounts:', err);
            setError('Failed to load discount data.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Unique states for filter dropdown
    const uniqueStates = useMemo(
        () =>
            Array.from(new Set(rows.map(r => r.state)))
                .filter(Boolean)
                .sort(),
        [rows]
    );

    // Filtered + sorted rows
    const displayRows = useMemo(() => {
        let filtered = rows;
        if (stateFilter) {
            filtered = filtered.filter(r => r.state === stateFilter);
        }

        return [...filtered].sort((a, b) => {
            const aVal = a[sortField];
            const bVal = b[sortField];

            if (aVal == null && bVal == null) return 0;
            if (aVal == null) return 1;
            if (bVal == null) return -1;

            let cmp: number;
            if (typeof aVal === 'boolean') {
                cmp = aVal === bVal ? 0 : aVal ? -1 : 1;
            } else if (typeof aVal === 'number') {
                cmp = (aVal as number) - (bVal as number);
            } else {
                cmp = String(aVal).localeCompare(String(bVal));
            }

            return sortDir === 'asc' ? cmp : -cmp;
        });
    }, [rows, stateFilter, sortField, sortDir]);

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDir('asc');
        }
    };

    const handleClearAll = async () => {
        if (
            !window.confirm(
                'Are you sure you want to delete ALL discount records? This cannot be undone.'
            )
        )
            return;

        try {
            const { error: delError } = await supabase
                .from('discount_manager')
                .delete()
                .neq('id', 0);
            if (delError) throw delError;
            setRows([]);
        } catch (err: any) {
            console.error('Failed to clear discounts:', err);
            setError('Failed to clear discount data.');
        }
    };

    const handleCellSave = async (id: number, field: string, value: any) => {
        const { error: updateErr } = await supabase
            .from('discount_manager')
            .update({ [field]: value })
            .eq('id', id);

        if (updateErr) {
            console.error('Failed to update:', updateErr);
            setSnackMsg('Failed to save change.');
            return;
        }

        setRows(prev =>
            prev.map(r => (r.id === id ? { ...r, [field]: value } : r))
        );
        setSnackMsg('Saved');
    };

    const handleDownloadSample = () => {
        const csv =
            'AIP,State,has_discount,discount,discount_deadline,premium_deadline,notes,excluded_crops,excluded_products\n' +
            'Great American,MT,yes,0.05,03/15/2026,04/01/2026,Early bird discount,,\n' +
            'NAU,ID,yes,0.08,08/15,10/31,Potatoes excluded,Potatoes,"XS10,XS15"\n';
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'discount_manager_sample.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    const columns: {
        field: SortField;
        label: string;
        width?: number;
    }[] = [
        { field: 'aip', label: 'AIP' },
        { field: 'state', label: 'State', width: 80 },
        {
            field: 'has_discount',
            label: 'Has Discount',
            width: 120,
        },
        { field: 'discount', label: 'Discount', width: 100 },
        {
            field: 'discount_deadline',
            label: 'Discount Deadline',
        },
        {
            field: 'premium_deadline',
            label: 'Premium Deadline',
        },
        { field: 'notes', label: 'Notes' },
        { field: 'excluded_crops', label: 'Excluded Crops' },
        { field: 'excluded_products', label: 'Excluded Products' },
    ];

    return (
        <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
            <Title title="Discounts" />

            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
            >
                <Typography variant="h5" fontWeight={600}>
                    Discounts
                </Typography>
                {isAdmin && (
                    <Stack direction="row" spacing={1}>
                        <Tooltip title="Download sample CSV">
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<DownloadIcon />}
                                onClick={handleDownloadSample}
                            >
                                Sample CSV
                            </Button>
                        </Tooltip>
                        {rows.length > 0 && (
                            <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                startIcon={<DeleteIcon />}
                                onClick={handleClearAll}
                            >
                                Clear All
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            startIcon={<UploadIcon />}
                            onClick={() => setUploadOpen(true)}
                        >
                            Upload CSV
                        </Button>
                    </Stack>
                )}
            </Stack>

            {/* Filter bar */}
            {rows.length > 0 && (
                <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    sx={{ mb: 2 }}
                >
                    <FilterIcon sx={{ color: 'text.secondary' }} />
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>State</InputLabel>
                        <Select
                            value={stateFilter}
                            label="State"
                            onChange={e => {
                                setStateFilter(e.target.value as string);
                                setPage(0);
                            }}
                        >
                            <MenuItem value="">
                                <em>All States</em>
                            </MenuItem>
                            {uniqueStates.map(s => (
                                <MenuItem key={s} value={s}>
                                    {s}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {stateFilter && (
                        <Chip
                            label={`State: ${stateFilter}`}
                            onDelete={() => {
                                setStateFilter('');
                                setPage(0);
                            }}
                            size="small"
                        />
                    )}
                    <Typography variant="body2" color="text.secondary">
                        {displayRows.length} record
                        {displayRows.length !== 1 ? 's' : ''}
                    </Typography>
                </Stack>
            )}

            {error && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    onClose={() => setError('')}
                >
                    {error}
                </Alert>
            )}

            {loading ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        p: 6,
                    }}
                >
                    <CircularProgress />
                </Box>
            ) : rows.length === 0 ? (
                <Paper
                    sx={{
                        p: 6,
                        textAlign: 'center',
                        color: 'text.secondary',
                    }}
                >
                    <UploadIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                    <Typography variant="h6" gutterBottom>
                        No discount data yet
                    </Typography>
                    {isAdmin ? (
                        <>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                Upload a CSV file to populate the discount
                                table.
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<UploadIcon />}
                                onClick={() => setUploadOpen(true)}
                            >
                                Upload CSV
                            </Button>
                        </>
                    ) : (
                        <Typography variant="body2">
                            An admin has not uploaded discount data yet.
                        </Typography>
                    )}
                </Paper>
            ) : (
                <Paper>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {columns.map(col => (
                                        <TableCell
                                            key={col.field}
                                            sx={{
                                                fontWeight: 700,
                                                ...(col.width
                                                    ? {
                                                          width: col.width,
                                                      }
                                                    : {}),
                                            }}
                                            sortDirection={
                                                sortField === col.field
                                                    ? sortDir
                                                    : false
                                            }
                                        >
                                            <TableSortLabel
                                                active={sortField === col.field}
                                                direction={
                                                    sortField === col.field
                                                        ? sortDir
                                                        : 'asc'
                                                }
                                                onClick={() =>
                                                    handleSort(col.field)
                                                }
                                            >
                                                {col.label}
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {displayRows
                                    .slice(
                                        page * rowsPerPage,
                                        page * rowsPerPage + rowsPerPage
                                    )
                                    .map(row => (
                                        <EditableRow
                                            key={row.id}
                                            row={row}
                                            editable={isAdmin}
                                            onSave={handleCellSave}
                                        />
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={displayRows.length}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </Paper>
            )}

            <UploadDialog
                open={uploadOpen}
                onClose={() => setUploadOpen(false)}
                onComplete={() => {
                    setUploadOpen(false);
                    fetchData();
                }}
            />

            <Snackbar
                open={!!snackMsg}
                autoHideDuration={2000}
                onClose={() => setSnackMsg('')}
                message={snackMsg}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
            />
        </Box>
    );
};

// ---------- Editable Row ----------

function EditableRow({
    row,
    editable,
    onSave,
}: {
    row: DiscountRow;
    editable: boolean;
    onSave: (id: number, field: string, value: any) => void;
}) {
    if (!editable) {
        return (
            <TableRow hover>
                <TableCell>{row.aip || '—'}</TableCell>
                <TableCell>{row.state || '—'}</TableCell>
                <TableCell>
                    <Chip
                        label={row.has_discount ? 'Yes' : 'No'}
                        color={row.has_discount ? 'success' : 'default'}
                        size="small"
                        variant="outlined"
                    />
                </TableCell>
                <TableCell>
                    {row.discount != null
                        ? `${(row.discount * 100).toFixed(
                              Number.isInteger(row.discount * 100) ? 0 : 2
                          )}%`
                        : '—'}
                </TableCell>
                <TableCell>{row.discount_deadline || '—'}</TableCell>
                <TableCell>{row.premium_deadline || '—'}</TableCell>
                <TableCell>{row.notes || '—'}</TableCell>
                <TableCell>
                    {row.excluded_crops?.length > 0
                        ? row.excluded_crops.map(crop => (
                              <Chip
                                  key={crop}
                                  label={crop}
                                  size="small"
                                  sx={{ mr: 0.5, mb: 0.5 }}
                              />
                          ))
                        : '—'}
                </TableCell>
                <TableCell>
                    {row.excluded_products?.length > 0
                        ? row.excluded_products.map(product => (
                              <Chip
                                  key={product}
                                  label={product}
                                  size="small"
                                  sx={{ mr: 0.5, mb: 0.5 }}
                              />
                          ))
                        : '—'}
                </TableCell>
            </TableRow>
        );
    }

    return (
        <TableRow hover>
            <EditableTextCell
                value={row.aip}
                onSave={val => onSave(row.id, 'aip', val)}
            />
            <EditableTextCell
                value={row.state}
                onSave={val => onSave(row.id, 'state', val)}
            />
            <TableCell>
                <Switch
                    checked={row.has_discount}
                    size="small"
                    onChange={e =>
                        onSave(row.id, 'has_discount', e.target.checked)
                    }
                />
            </TableCell>
            <EditablePercentCell
                value={row.discount}
                onSave={val => onSave(row.id, 'discount', val)}
            />
            <EditableTextCell
                value={row.discount_deadline ?? ''}
                onSave={val => onSave(row.id, 'discount_deadline', val || null)}
            />
            <EditableTextCell
                value={row.premium_deadline ?? ''}
                onSave={val => onSave(row.id, 'premium_deadline', val || null)}
            />
            <EditableTextCell
                value={row.notes ?? ''}
                onSave={val => onSave(row.id, 'notes', val || null)}
            />
            <EditableExcludedCropsCell
                value={row.excluded_crops ?? []}
                state={row.state}
                onSave={val => onSave(row.id, 'excluded_crops', val)}
            />
            <EditableExcludedProductsCell
                value={row.excluded_products ?? []}
                onSave={val => onSave(row.id, 'excluded_products', val)}
            />
        </TableRow>
    );
}

// ---------- Editable Cells ----------

function EditableTextCell({
    value,
    onSave,
}: {
    value: string;
    onSave: (val: string) => void;
}) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value);

    const commit = () => {
        setEditing(false);
        if (draft !== value) {
            onSave(draft);
        }
    };

    if (editing) {
        return (
            <TableCell sx={{ p: 0.5 }}>
                <TextField
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    onBlur={commit}
                    onKeyDown={e => {
                        if (e.key === 'Enter') commit();
                        if (e.key === 'Escape') {
                            setDraft(value);
                            setEditing(false);
                        }
                    }}
                    size="small"
                    variant="standard"
                    autoFocus
                    fullWidth
                    sx={{ '& input': { fontSize: '0.875rem' } }}
                />
            </TableCell>
        );
    }

    return (
        <TableCell
            onClick={() => {
                setDraft(value);
                setEditing(true);
            }}
            sx={{
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
            }}
        >
            {value || '—'}
        </TableCell>
    );
}

function EditablePercentCell({
    value,
    onSave,
}: {
    value: number | null;
    onSave: (val: number | null) => void;
}) {
    const [editing, setEditing] = useState(false);
    // Display as percentage (0.05 → "5")
    const displayPct = value != null ? (value * 100).toFixed(2) : '';
    const [draft, setDraft] = useState(displayPct);

    const commit = () => {
        setEditing(false);
        const trimmed = draft.trim();
        if (trimmed === '') {
            if (value !== null) onSave(null);
            return;
        }
        const num = parseFloat(trimmed);
        if (!isNaN(num)) {
            const decimal = num / 100; // Convert back: 5 → 0.05
            if (decimal !== value) onSave(decimal);
        }
    };

    if (editing) {
        return (
            <TableCell sx={{ p: 0.5 }}>
                <TextField
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    onBlur={commit}
                    onKeyDown={e => {
                        if (e.key === 'Enter') commit();
                        if (e.key === 'Escape') {
                            setDraft(displayPct);
                            setEditing(false);
                        }
                    }}
                    size="small"
                    variant="standard"
                    autoFocus
                    fullWidth
                    InputProps={{
                        endAdornment: (
                            <Typography variant="body2" color="text.secondary">
                                %
                            </Typography>
                        ),
                    }}
                    sx={{ '& input': { fontSize: '0.875rem' } }}
                />
            </TableCell>
        );
    }

    return (
        <TableCell
            onClick={() => {
                setDraft(displayPct);
                setEditing(true);
            }}
            sx={{
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
            }}
        >
            {value != null
                ? `${(value * 100).toFixed(
                      Number.isInteger(value * 100) ? 0 : 2
                  )}%`
                : '—'}
        </TableCell>
    );
}

// ---------- Editable Excluded Crops Cell ----------

function EditableExcludedCropsCell({
    value,
    state,
    onSave,
}: {
    value: string[];
    state: string;
    onSave: (val: string[]) => void;
}) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState<string[]>(value);
    const [options, setOptions] = useState<string[]>([]);

    useEffect(() => {
        if (!editing || !state) return;
        supabase
            .from('crop_classes')
            .select('crop')
            .eq('state', state)
            .then(({ data }) => {
                const crops = Array.from(
                    new Set(
                        (data ?? []).map((d: any) => d.crop).filter(Boolean)
                    )
                ).sort() as string[];
                setOptions(crops);
            });
    }, [editing, state]);

    const commit = () => {
        setEditing(false);
        const sorted = [...draft].sort();
        const origSorted = [...value].sort();
        if (JSON.stringify(sorted) !== JSON.stringify(origSorted)) {
            onSave(draft);
        }
    };

    if (editing) {
        return (
            <TableCell sx={{ p: 0.5, minWidth: 200 }}>
                <Autocomplete
                    multiple
                    freeSolo
                    size="small"
                    options={options}
                    value={draft}
                    onChange={(_, newVal) => setDraft(newVal as string[])}
                    onBlur={commit}
                    renderTags={(val, getTagProps) =>
                        val.map((crop, idx) => (
                            <Chip
                                {...getTagProps({ index: idx })}
                                key={crop}
                                label={crop}
                                size="small"
                            />
                        ))
                    }
                    renderInput={params => (
                        <TextField
                            {...params}
                            variant="standard"
                            autoFocus
                            placeholder="Select crops..."
                        />
                    )}
                    autoFocus
                    openOnFocus
                />
            </TableCell>
        );
    }

    return (
        <TableCell
            onClick={() => {
                setDraft(value);
                setEditing(true);
            }}
            sx={{
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
                minWidth: 150,
            }}
        >
            {value.length > 0
                ? value.map(crop => (
                      <Chip
                          key={crop}
                          label={crop}
                          size="small"
                          sx={{ mr: 0.5, mb: 0.5 }}
                      />
                  ))
                : '—'}
        </TableCell>
    );
}

// ---------- Editable Excluded Products Cell ----------

function EditableExcludedProductsCell({
    value,
    onSave,
}: {
    value: string[];
    onSave: (val: string[]) => void;
}) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState<string[]>(value);
    const [options, setOptions] = useState<string[]>([]);

    useEffect(() => {
        if (!editing) return;
        supabase
            .from('crop_coverages')
            .select('coverage')
            .then(({ data }) => {
                const products = Array.from(
                    new Set(
                        (data ?? []).map((d: any) => d.coverage).filter(Boolean)
                    )
                ).sort() as string[];
                setOptions(products);
            });
    }, [editing]);

    const commit = () => {
        setEditing(false);
        const sorted = [...draft].sort();
        const origSorted = [...value].sort();
        if (JSON.stringify(sorted) !== JSON.stringify(origSorted)) {
            onSave(draft);
        }
    };

    if (editing) {
        return (
            <TableCell sx={{ p: 0.5, minWidth: 200 }}>
                <Autocomplete
                    multiple
                    freeSolo
                    size="small"
                    options={options}
                    value={draft}
                    onChange={(_, newVal) => setDraft(newVal as string[])}
                    onBlur={commit}
                    renderTags={(val, getTagProps) =>
                        val.map((product, idx) => (
                            <Chip
                                {...getTagProps({ index: idx })}
                                key={product}
                                label={product}
                                size="small"
                            />
                        ))
                    }
                    renderInput={params => (
                        <TextField
                            {...params}
                            variant="standard"
                            autoFocus
                            placeholder="Select products..."
                        />
                    )}
                    autoFocus
                    openOnFocus
                />
            </TableCell>
        );
    }

    return (
        <TableCell
            onClick={() => {
                setDraft(value);
                setEditing(true);
            }}
            sx={{
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
                minWidth: 150,
            }}
        >
            {value.length > 0
                ? value.map(product => (
                      <Chip
                          key={product}
                          label={product}
                          size="small"
                          sx={{ mr: 0.5, mb: 0.5 }}
                      />
                  ))
                : '—'}
        </TableCell>
    );
}

// ---------- Upload Dialog ----------

function UploadDialog({
    open,
    onClose,
    onComplete,
}: {
    open: boolean;
    onClose: () => void;
    onComplete: () => void;
}) {
    const { processBatch } = useDiscountImport();
    const { importer, parseCsv, reset } = usePapaParse<DiscountCsvRow>({
        batchSize: 50,
        processBatch,
    });

    const onDrop = useCallback(
        (accepted: File[]) => {
            if (accepted.length > 0) {
                parseCsv(accepted[0]);
            }
        },
        [parseCsv]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'text/csv': ['.csv'],
            'application/vnd.ms-excel': ['.csv'],
        },
        multiple: false,
        disabled: importer.state === 'parsing' || importer.state === 'running',
    });

    const handleClose = () => {
        if (importer.state === 'complete') {
            onComplete();
        } else {
            onClose();
        }
        reset();
    };

    const progress =
        importer.state === 'running' || importer.state === 'complete'
            ? importer.rowCount > 0
                ? (importer.importCount / importer.rowCount) * 100
                : 0
            : 0;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Upload Discount CSV</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <Alert severity="info">
                        Expected columns: <strong>AIP</strong>,{' '}
                        <strong>State</strong>, <strong>has_discount</strong>{' '}
                        (yes/no), <strong>discount</strong> (decimal e.g. 0.05),{' '}
                        <strong>discount_deadline</strong>,{' '}
                        <strong>premium_deadline</strong>,{' '}
                        <strong>notes</strong>, <strong>excluded_crops</strong>
                    </Alert>

                    <Box
                        {...getRootProps()}
                        sx={{
                            border: '2px dashed',
                            borderColor: isDragActive ? 'primary.main' : '#ccc',
                            borderRadius: 2,
                            p: 4,
                            textAlign: 'center',
                            cursor:
                                importer.state === 'running'
                                    ? 'default'
                                    : 'pointer',
                            bgcolor: isDragActive
                                ? 'action.hover'
                                : 'transparent',
                            transition: 'all 0.2s',
                        }}
                    >
                        <input {...getInputProps()} />
                        <UploadIcon
                            sx={{
                                fontSize: 40,
                                color: 'text.secondary',
                                mb: 1,
                            }}
                        />
                        <Typography>
                            {isDragActive
                                ? 'Drop the CSV file here...'
                                : 'Drag & drop a CSV file here, or click to select'}
                        </Typography>
                    </Box>

                    {importer.state === 'parsing' && (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <CircularProgress size={20} />
                            <Typography variant="body2">
                                Parsing CSV...
                            </Typography>
                        </Stack>
                    )}

                    {(importer.state === 'running' ||
                        importer.state === 'complete') && (
                        <Box>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                sx={{ mb: 0.5 }}
                            >
                                <Typography variant="body2">
                                    {importer.state === 'complete'
                                        ? 'Upload complete!'
                                        : `Importing ${importer.importCount} of ${importer.rowCount} rows...`}
                                </Typography>
                                {importer.remainingTime != null && (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        ~
                                        {Math.ceil(
                                            importer.remainingTime / 1000
                                        )}
                                        s remaining
                                    </Typography>
                                )}
                            </Stack>
                            <LinearProgress
                                variant="determinate"
                                value={progress}
                                sx={{
                                    height: 8,
                                    borderRadius: 4,
                                }}
                            />
                            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                                <Typography
                                    variant="body2"
                                    color="success.main"
                                >
                                    ✓ {importer.importCount} imported
                                </Typography>
                                {importer.errorCount > 0 && (
                                    <Typography
                                        variant="body2"
                                        color="error.main"
                                    >
                                        ✗ {importer.errorCount} errors
                                    </Typography>
                                )}
                            </Stack>
                        </Box>
                    )}

                    {importer.state === 'error' && (
                        <Alert severity="error">
                            Failed to parse CSV: {importer.error.message}
                        </Alert>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>
                    {importer.state === 'complete' ? 'Done' : 'Cancel'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default DiscountManagerPage;
